import React, { useState } from 'react';
import { Button, Dropdown, Form, Input, Modal, Select, Typography } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../context/AuthContext';
import {
  useInviteUserMutation,
  useRemoveUserMutation,
  useResetUserPasswordMutation,
  useTeamQuery,
  useUpdateUserMutation
} from '../../queries/users';
import { ACCESS_ROLES, ROLE_HINTS } from '../../utils/roles';
import { TEAM_ROLES } from '../../utils/actions';
import { Card, CardHead, TonePill } from '../../components';
import { HeadText, StackTable } from './styled';

const TEAM_ROLE_OPTIONS = [{ value: '', label: 'None' }, ...TEAM_ROLES.map(([value, label]) => ({ value, label }))];

const fullName = (u) => u.fullName || `${u.firstName || ''} ${u.lastName || ''}`.trim();

function TempPassword({ email, password }) {
  return (
    <div>
      <p style={{ marginBottom: 4 }}>We emailed this one-time password to {email}:</p>
      <Typography.Text code copyable>
        {password}
      </Typography.Text>
      <p style={{ marginTop: 8, marginBottom: 0, color: '#8c8c8c' }}>They will set their own password at first login.</p>
    </div>
  );
}

const showPassword = (title, email, password) =>
  Modal.success({ title, content: <TempPassword email={email} password={password} /> });

function InviteModal({ open, isOwner, onClose }) {
  const [form] = Form.useForm();
  const role = Form.useWatch('role', form) || 'staff';
  const { mutate, isPending } = useInviteUserMutation();

  const submit = (values) =>
    mutate(values, {
      onSuccess: (res) => {
        form.resetFields();
        onClose();
        showPassword('Team member added', res.user.email, res.tempPassword);
      }
    });

  return (
    <Modal
      title="Add team member"
      open={open}
      onCancel={onClose}
      onOk={form.submit}
      okText="Add to team"
      confirmLoading={isPending}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" onFinish={submit} requiredMark={false} initialValues={{ role: 'staff', teamRole: '' }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <Form.Item name="firstName" label="First name" rules={[{ required: true, whitespace: true }]} style={{ flex: 1 }}>
            <Input />
          </Form.Item>
          <Form.Item name="lastName" label="Last name" rules={[{ required: true, whitespace: true }]} style={{ flex: 1 }}>
            <Input />
          </Form.Item>
        </div>
        <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
          <Input placeholder="teammate@company.com" />
        </Form.Item>
        <Form.Item name="role" label="Role" extra={ROLE_HINTS[role]}>
          <Select options={ACCESS_ROLES.filter((r) => isOwner || r.value !== 'owner')} />
        </Form.Item>
        <Form.Item name="teamRole" label="Team role" extra="Agent Actions waiting on this role go to this person.">
          <Select options={TEAM_ROLE_OPTIONS} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default function TeamCard() {
  const { user: me } = useAuth();
  const isOwner = me?.role === 'owner' || me?.userType === 'platform_admin';
  const { data: users = [], isPending } = useTeamQuery();
  const update = useUpdateUserMutation();
  const reset = useResetUserPasswordMutation();
  const remove = useRemoveUserMutation();
  const [inviting, setInviting] = useState(false);

  const act = (u, key) => {
    if (key === 'reset') {
      reset.mutate(u._id, { onSuccess: (res) => showPassword('Password reset', u.email, res.tempPassword) });
    } else if (key === 'suspend' || key === 'activate') {
      update.mutate({ id: u._id, status: key === 'suspend' ? 'suspended' : 'active' });
    } else if (key === 'remove') {
      Modal.confirm({
        title: `Remove ${fullName(u)}?`,
        content: 'They will lose access to the portal right away.',
        okText: 'Remove',
        okButtonProps: { danger: true },
        onOk: () => remove.mutateAsync(u._id)
      });
    }
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, u) => (
        <div>
          <strong style={{ color: '#262626' }}>{fullName(u)}</strong>
          {String(u._id) === String(me?._id) && <TonePill $tone="info" style={{ marginLeft: 8 }}>You</TonePill>}
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>{u.email}</div>
        </div>
      )
    },
    {
      title: 'Role',
      key: 'role',
      width: 160,
      render: (_, u) => {
        const locked = String(u._id) === String(me?._id) || (!isOwner && u.role === 'owner');
        return (
          <Select
            aria-label={`Role for ${fullName(u)}`}
            size="small"
            value={u.role}
            disabled={locked}
            onChange={(role) => update.mutate({ id: u._id, role })}
            options={ACCESS_ROLES.filter((r) => isOwner || r.value !== 'owner' || u.role === 'owner')}
            style={{ width: 120 }}
          />
        );
      }
    },
    {
      title: 'Team role',
      key: 'teamRole',
      width: 210,
      render: (_, u) => (
        <Select
          aria-label={`Team role for ${fullName(u)}`}
          size="small"
          value={u.teamRole || ''}
          onChange={(teamRole) => update.mutate({ id: u._id, teamRole })}
          options={TEAM_ROLE_OPTIONS}
          style={{ width: 190 }}
        />
      )
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, u) => (
        <TonePill $tone={u.status === 'active' ? 'success' : 'danger'}>{u.status === 'active' ? 'Active' : 'Suspended'}</TonePill>
      )
    },
    {
      key: 'actions',
      width: 56,
      render: (_, u) => {
        const isSelf = String(u._id) === String(me?._id);
        if (isSelf || (!isOwner && u.role === 'owner')) return null;
        const items = [
          { key: 'reset', label: 'Reset password' },
          u.status === 'active' ? { key: 'suspend', label: 'Suspend', danger: true } : { key: 'activate', label: 'Activate' },
          { type: 'divider' },
          { key: 'remove', label: 'Remove', danger: true }
        ];
        return (
          <Dropdown trigger={['click']} menu={{ items, onClick: ({ key }) => act(u, key) }}>
            <Button type="text" aria-label={`Actions for ${fullName(u)}`} icon={<FontAwesomeIcon icon={faEllipsisVertical} />} />
          </Dropdown>
        );
      }
    }
  ];

  return (
    <Card aria-labelledby="team-title">
      <CardHead style={{ justifyContent: 'space-between' }}>
        <HeadText>
          <h2 id="team-title">Team members</h2>
          <span>{users.length} total · role sets what each person can see and do</span>
        </HeadText>
        <Button type="primary" icon={<FontAwesomeIcon icon={faUserPlus} />} onClick={() => setInviting(true)}>
          Add member
        </Button>
      </CardHead>
      <StackTable
        rowKey="_id"
        size="middle"
        loading={isPending}
        columns={columns}
        dataSource={users}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        scroll={{ x: 560 }}
      />
      <InviteModal open={inviting} isOwner={isOwner} onClose={() => setInviting(false)} />
    </Card>
  );
}
